import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { useEmail } from '../../../contexts/EmailContext';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import { ESCALATION_LEVELS } from '../../../utils/deadlineRules';

const { 
  FiArrowLeft, FiSave, FiGlobe, FiBell, FiServer, 
  FiCheck, FiRefreshCw, FiSend, FiClock, FiAlertTriangle 
} = FiIcons;

const SystemSettings = () => {
  const navigate = useNavigate();
  const emailContext = useEmail();
  const { user } = useAuth();
  const { resetData } = useData();
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testEmailStatus, setTestEmailStatus] = useState(null); // 'sending', 'success', 'error'

  const [settings, setSettings] = useState({
    siteName: 'HMS Varsling',
    emailServiceId: '',
    emailPublicKey: ''
  });

  useEffect(() => {
    if (emailContext?.emailSettings) {
      setSettings(prev => ({
        ...prev,
        emailServiceId: emailContext.emailSettings.serviceId || '',
        emailPublicKey: emailContext.emailSettings.publicKey || ''
      }));
    }
  }, [emailContext]);

  const handleSave = () => {
    setLoading(true);
    if (emailContext?.saveEmailSettings) {
      emailContext.saveEmailSettings({
        ...emailContext.emailSettings,
        serviceId: settings.emailServiceId,
        publicKey: settings.emailPublicKey
      });
    }
    
    setTimeout(() => {
      setLoading(false);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    }, 800);
  };

  const handleTestEmail = async () => {
    if (!settings.emailServiceId || !settings.emailPublicKey) {
      alert("Du må lagre Service ID og Public Key før du tester.");
      return;
    }
    
    setTestEmailStatus('sending');
    try {
        // Use the welcome email as a test template
        await emailContext.sendWelcomeEmail(
            user?.email || 'test@example.com', 
            user?.name || 'Admin', 
            'superadmin', 
            'HMS Varsling System'
        );
        // Force process queue to send immediately
        emailContext.processQueueManually();
        
        setTestEmailStatus('success');
        setTimeout(() => setTestEmailStatus(null), 3000);
    } catch (error) {
        console.error("Test email failed:", error);
        setTestEmailStatus('error');
    }
  };

  const tabs = [
    { id: 'general', label: 'Generelt', icon: FiGlobe },
    { id: 'notifications', label: 'Varsling', icon: FiBell },
    { id: 'deadlines', label: 'Fristregler', icon: FiClock },
    { id: 'system', label: 'System', icon: FiServer },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 h-16 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/superadmin')} className="p-2 hover:bg-gray-100 rounded-lg">
              <SafeIcon icon={FiArrowLeft} />
            </button>
            <h1 className="text-xl font-bold text-gray-900">Systeminnstillinger</h1>
          </div>
          <button 
            onClick={handleSave}
            disabled={loading}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              saved ? 'bg-green-600 text-white' : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {loading ? <SafeIcon icon={FiRefreshCw} className="animate-spin" /> : saved ? <SafeIcon icon={FiCheck} /> : <SafeIcon icon={FiSave} />}
            {saved ? 'Lagret' : 'Lagre'}
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8 flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-64 flex-shrink-0">
          <div className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-100">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-sm font-medium transition-colors ${
                  activeTab === tab.id ? 'bg-blue-50 text-blue-600 border-r-2 border-blue-600' : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <SafeIcon icon={tab.icon} className="text-lg" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 bg-white rounded-xl shadow-sm p-6 min-h-[400px] border border-gray-100">
          {activeTab === 'general' && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-gray-900">Generelt</h2>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Systemnavn</label>
                <input 
                  type="text" 
                  value={settings.siteName}
                  onChange={(e) => setSettings({...settings, siteName: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          )}

          {activeTab === 'notifications' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                  <h2 className="text-lg font-bold text-gray-900">E-postinnstillinger (EmailJS)</h2>
                  <button 
                    onClick={handleTestEmail}
                    disabled={testEmailStatus === 'sending'}
                    className={`text-sm px-3 py-1.5 rounded-lg border flex items-center gap-2 ${
                        testEmailStatus === 'success' ? 'bg-green-50 text-green-700 border-green-200' : 
                        testEmailStatus === 'error' ? 'bg-red-50 text-red-700 border-red-200' :
                        'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    {testEmailStatus === 'sending' ? (
                        <SafeIcon icon={FiRefreshCw} className="animate-spin" />
                    ) : testEmailStatus === 'success' ? (
                        <SafeIcon icon={FiCheck} />
                    ) : (
                        <SafeIcon icon={FiSend} />
                    )}
                    {testEmailStatus === 'success' ? 'Sendt!' : 'Test oppsett'}
                  </button>
              </div>
              
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Service ID</label>
                  <input 
                    type="text" 
                    value={settings.emailServiceId}
                    onChange={(e) => setSettings({...settings, emailServiceId: e.target.value})}
                    placeholder="service_xxxx"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Public Key</label>
                  <input 
                    type="password" 
                    value={settings.emailPublicKey}
                    onChange={(e) => setSettings({...settings, emailPublicKey: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              
              <div className="bg-blue-50 p-4 rounded-lg text-sm text-blue-800">
                <p className="font-bold mb-1">Husk Templates!</p>
                <p>For at e-post skal fungere, må du ha opprettet templates i EmailJS med følgende ID-er:</p>
                <ul className="list-disc pl-5 mt-2 space-y-1 text-xs">
                    <li>template_welcome</li>
                    <li>template_new_report</li>
                    <li>template_receipt</li>
                </ul>
              </div>
            </div>
          )}

          {activeTab === 'deadlines' && (
            <div className="space-y-8">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <SafeIcon icon={FiClock} className="text-xl" />
                </div>
                <h2 className="text-lg font-bold text-gray-900">Lovpålagte Fristregler</h2>
              </div>

              <div className="grid gap-6">
                <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiCheck} className="text-green-600" />
                    Mottaksbekreftelse (7 dager)
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Normal</span>
                      <span className="text-green-700 font-bold px-2 py-0.5 bg-green-100 rounded">0 - 5 dager</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Haster</span>
                      <span className="text-amber-700 font-bold px-2 py-0.5 bg-amber-100 rounded">5 - 7 dager</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Forsinket (Eskaleres)</span>
                      <span className="text-red-700 font-bold px-2 py-0.5 bg-red-100 rounded">&gt; 7 dager</span>
                    </div>
                  </div>
                </div>

                <div className="p-6 bg-gray-50 rounded-xl border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiCheck} className="text-blue-600" />
                    Tilbakemelding (3 måneder)
                  </h3>
                  <div className="space-y-4">
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Normal</span>
                      <span className="text-green-700 font-bold px-2 py-0.5 bg-green-100 rounded">0 - 83 dager</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Haster</span>
                      <span className="text-amber-700 font-bold px-2 py-0.5 bg-amber-100 rounded">&lt; 7 dager igjen</span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 font-medium">Forsinket (Eskaleres)</span>
                      <span className="text-red-700 font-bold px-2 py-0.5 bg-red-100 rounded">&gt; 90 dager</span>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg border border-amber-200 flex gap-3">
                  <SafeIcon icon={FiAlertTriangle} className="text-amber-600 mt-1 flex-shrink-0" />
                  <div className="text-sm text-amber-800">
                    <p className="font-bold mb-1">Automatisk Eskalering</p>
                    <p>Systemet overvåker disse fristene kontinuerlig. Saker som går i "KRITISK" status (Level 3) vil bli flagget for SuperAdmin automatisk.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'system' && (
            <div className="space-y-6">
              <h2 className="text-lg font-bold text-gray-900">Systemvedlikehold</h2>
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h3 className="text-red-900 font-bold mb-2">Tilbakestill system</h3>
                <p className="text-sm text-red-700 mb-4">Dette sletter lokal lagring og starter på nytt.</p>
                <button 
                  onClick={resetData}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg font-bold flex items-center gap-2"
                >
                  <SafeIcon icon={FiRefreshCw} /> Tilbakestill
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SystemSettings;